export type Tome = {
  label: string;
  emoji: string;
  title: string;
  subtitle: string;
  intro?: string;
  accent: 'blue' | 'green' | 'gold';
  cover: string;
  coverAlt: string;
  features: string[];
  note?: string;
  warning?: string;
};

export type Bonus = {
  number: string;
  title: string;
  pages: string;
  description: string;
  cover: string;
  coverAlt: string;
  features: string[];
};

/** The 3 core volumes of the Biodecoding Practitioner Kit. */
export const tomes: Tome[] = [
  {
    label: 'VOLUME I',
    emoji: '📘',
    title: 'Ethical Practice & Professional Boundaries',
    subtitle: 'A Clear Framework for Consent, Scope and Confident Client Support',
    accent: 'blue',
    cover: 'https://cdn.magicpatterns.com/uploads/2Dw2idv2ZFeUhgokRg9RW2/TOMO1.png',
    coverAlt: 'Volume I cover: Ethical Practice & Professional Boundaries',
    features: [
      'A clear professional framework for educational and reflective practice',
      'How to establish and communicate your scope with confidence',
      'Informed-consent principles for practitioner settings',
      'Client communication boundaries before, during, and after a session',
      'Ready-to-use language for difficult conversations',
      'Ethical referral guidance: when to pause and how to suggest appropriate support',
      'Documentation and follow-up principles for a more organised practice',
    ],
    note: '50 pages of frameworks, scripts, and resources to work with greater clarity within your professional role',
  },
  {
    label: 'VOLUME II',
    emoji: '📗',
    title: 'Bioshock Protocols & Express Intake',
    subtitle: 'A Repeatable Session Flow from Symptom Language to Emotional Context',
    intro:
      '“Bioshock” is a term used in biodecoding to describe an unexpected, emotionally overwhelming experience. This volume provides questions and visual maps to explore the client’s reported symptoms, emotional context, timelines, and repeated patterns.',
    accent: 'green',
    cover: 'https://cdn.magicpatterns.com/uploads/3kVoFzcbKNTy7dpKw9Rr67/Volumen_II__mapa_de_sesion-2.png',
    coverAlt: 'Volume II cover: Bioshock Protocols & Express Intake',
    features: [
      'Practical foundations of the bioshock approach',
      'A structured flow for organising the client’s emotional context',
      'Focused questions to guide the opening of a session',
      'Symptom-language and body-system maps as exploratory resources',
      'A visual family-pattern map to organise relevant background information',
      'Timeline prompts for events, patterns, and recurring themes',
      'Session scripts: questions and practitioner language for every stage',
      'Guided examples for applying the framework with more structure',
    ],
    note: '50 pages of organised tools for quick session consultation',
  },
  {
    label: 'VOLUME III',
    emoji: '📕',
    title: 'Symbolic Integration Practices',
    subtitle: '15 Guided Exercises to Transform Awareness into Action',
    accent: 'gold',
    cover: 'https://cdn.magicpatterns.com/uploads/vT3Cc4X1fxGBTZ4T8Qnn9a/TOMO_3Portada_premium_de_integracion_simbolica-3.png',
    coverAlt: 'Volume III cover: Symbolic Integration Practices',
    features: [
      'Foundations for using symbolic practices as reflective resources',
      'How to choose a practice according to the focus of the session',
      '15 guided exercises for writing, visualisation, movement, and closing',
      'Reflection prompts for personal meaning and integration',
      'Gentle breathing, grounding, and self-compassion resources',
      'Integration pathways for what emerges after a session',
      'Follow-up indicators and questions for the next conversation',
    ],
    note: '50 pages of practical exercises and ready-to-use resources',
  },
];

/** The 5 strategic bonuses, always included. */
export const bonuses: Bonus[] = [
  {
    number: '01',
    title: 'The Client Reflection Workbook™',
    pages: '25 pages',
    description:
      'A pre-session companion for organising personal history, family context, relevant events, symptom language, and the client’s intention for the session.',
    cover: 'https://cdn.magicpatterns.com/uploads/7MQxrKL3q8rbYLKyNX7j9a/Cuaderno_de_reflexion_terapeutica_premium-2.png',
    coverAlt: 'Bonus 01 cover: The Client Reflection Workbook',
    features: [],
  },
  {
    number: '02',
    title: '21-Day Integration Journal™',
    pages: '25 pages',
    description:
      'A post-session journal with 21 guided daily practices for noticing reflections, emotions, habits, and meaningful changes over time.',
    cover: 'https://cdn.magicpatterns.com/uploads/rmbBhgWHB22tedr1nM73XH/Diario_de_integracion_de_21_dias-3.png',
    coverAlt: 'Bonus 02 cover: 21-Day Integration Journal',
    features: [],
  },
  {
    number: '03',
    title: 'Practitioner Question Compass™',
    pages: '20 pages',
    description:
      '200+ focused prompts organised around context, symptom language, timelines, family patterns, integration, and closing — so you never have to improvise the next useful question.',
    cover: 'https://cdn.magicpatterns.com/uploads/vBf7sMbkoJSxYpezuY5YLu/Brujula_de_preguntas_para_biodecodificacion-4.png',
    coverAlt: 'Bonus 03 cover: Practitioner Question Compass',
    features: [],
  },
  {
    number: '04',
    title: 'Ethical Client Communication Guide™',
    pages: '15 pages',
    description:
      'Clear, compassionate scripts for every stage of a biodecoding practice: session agreements, boundaries, referrals, follow-up messages, and difficult conversations.',
    cover: 'https://cdn.magicpatterns.com/uploads/dRgsFEcfxxc7SRk7oJyB5A/Guia_etica_de_comunicacion_con_clientes-5.png',
    coverAlt: 'Bonus 04 cover: Ethical Client Communication Guide',
    features: [],
  },
  {
    number: '05',
    title: 'Red Flag Referral Checklist™',
    pages: '10 pages',
    description:
      'A practical decision guide for recognising when to pause, hold your scope, recommend appropriate support, and protect the client relationship with care.',
    cover: 'https://cdn.magicpatterns.com/uploads/5GdfVZaSqWsUHXZmGyr3fX/Bonus_5__Guia_de_senales_de_alerta-6.png',
    coverAlt: 'Bonus 05 cover: Red Flag Referral Checklist',
    features: [],
  },
];
